import type { MatchState, RestartKind } from '@fafi/shared';

/**
 * D142 (playtest 5, reports 1/2/5/8) — the retry loop for a drill or a lesson.
 *
 * D128's rule was stated correctly in prose and never implemented: *"an outcome is the ball going dead
 * — a goal, or any restart **the drill did not itself set up**"*. The shipped predicate was
 * `s.phase !== 'PLAY'`, which counts the scenario's OWN pending set piece as an outcome. A lesson whose
 * `setup` ends in `startRestart` is therefore "dead" from tick 1, and the board re-arms on a fixed
 * timer forever.
 *
 * D138 then halved the lesson delay to 60 ticks and turned a latent bug into a hard block, because the
 * re-arm now out-races the ceremonies it is interrupting:
 *
 * | scenario | ceremony | re-arm | result before this fix |
 * | --- | --- | --- | --- |
 * | penalty | 90 t | 61 t | `inCeremony` NEVER expires ⇒ every kick refused, forever |
 * | free kick / corner | 45 t | 61 t | ~0.5 s of takeable time per 2 s cycle |
 * | halftime | 300 t | 61 t | `endHalf` re-fires with `half === 1` ⇒ sides swap on a loop |
 *
 * The situation the scenario handed you is not an outcome — it owns the clock until it is taken, or
 * until the sim's own `RESTART_MAX_TICKS` rescue expires and the phase machine moves on by itself. No
 * new tunable: the retry budget is the config that already governs restarts.
 */
export interface ArmedRestart {
  readonly kind: RestartKind;
  readonly x: number;
  readonly y: number;
  readonly taker: number | null;
}

/** The restart a scenario's `setup` just armed, as a comparable value (`step()` clones state). */
export function armedOf(s: MatchState): ArmedRestart | null {
  const r = s.restart;
  return r === null ? null : { kind: r.kind, x: r.spot.x, y: r.spot.y, taker: r.taker };
}

/** Is the pending restart still the one the scenario armed, rather than one play produced? */
export function isArmedRestart(s: MatchState, armed: ArmedRestart | null): boolean {
  const r = s.restart;
  if (armed === null || r === null) return false;
  return r.kind === armed.kind && r.spot.x === armed.x && r.spot.y === armed.y && r.taker === armed.taker;
}

/**
 * D145 — a rep can end without the ball ever going dead.
 *
 * `LessonSpec.resetOn` was declared, documented and read by nothing. It matters most on a walled pitch:
 * at `teamSize <= WALLED_MAX_SIZE` the game is cage football, so `detectOutOfPlay` never fires and a
 * missed shot rebounds off the boards and rolls. `volley-finish` is 5-a-side, so a volley skied over the
 * bar produced **no** dead ball, **no** restart and therefore no re-arm — the playtester was left with a
 * ball trickling around and no way to have another go.
 *
 * `'outcome'` adds one honest end-of-rep signal to the dead-ball rule: you have TOUCHED the ball this
 * rep, you no longer have it, and it has come to rest. The touch requirement is what keeps it safe —
 * without it, every lesson that starts with a loose ball at your feet (`first-touch`: *"Walk onto the
 * ball to take it"*) would re-arm on tick 1, forever.
 */
const REP_SPENT_SPEED = 1.5; // m/s — a ball this slow is not going to be volleyed, headed or chased down

export class ScenarioReset {
  private armed: ArmedRestart | null = null;
  private deadSince: number | null = null;
  private touched = false;
  private wasArmed = false; // this scenario opens on a set piece — its rep ends when the ball stops
  private armTick = 0;

  /**
   * @param delayTicks dead ticks before the board is re-armed (sim ticks — never wall time).
   * @param resetOn    `'dead-ball'` waits for the ball to go dead; `'outcome'` also ends a rep that
   *                   fizzled out on a walled pitch; `'never'` leaves the board alone.
   * @param seat       the entity the human drives — "you touched it" is about him, not his side.
   */
  constructor(
    private readonly delayTicks: number,
    private readonly resetOn: 'dead-ball' | 'outcome' | 'never' = 'dead-ball',
    private readonly seat = -1,
  ) {}

  /** Call immediately after the scenario's `setup` has run: records what it put on the board. */
  arm(s: MatchState): void {
    this.armed = armedOf(s);
    this.wasArmed = this.armed !== null;
    this.deadSince = null;
    this.touched = false;
    this.armTick = s.tick;
  }

  /** D152: has the human played the ball yet this rep? (Drives the opponent hold.) */
  hasTouched(): boolean {
    return this.touched;
  }

  /** D152: ticks since the board was armed — the cap on how long the opposition may be held. */
  sinceArm(s: MatchState): number {
    return s.tick - this.armTick;
  }

  /** Has this rep been played at all? Set once the human has had the ball or struck it. */
  private noteTouch(s: MatchState): void {
    if (this.touched || this.seat < 0) return;
    if (s.ball.possessor === this.seat || s.ball.lastTouch?.entity === this.seat) this.touched = true;
  }

  /**
   * The rep is over. Two shapes, because a set piece and a passage of play end differently.
   *
   * A SET-PIECE rep (the scenario armed a restart, and it has since been taken or timed out) ends as
   * soon as the ball stops travelling — whoever has it. "Five balls, one keeper" means a fresh ball
   * after each one; without this, saving a penalty and letting the taker collect his own rebound left
   * him standing in the box holding it forever, and the lesson never served the next.
   *
   * An OPEN-PLAY rep ends only when he has played it and lost it: he touched the ball this rep, it is
   * not his any more, and it has come to rest. The touch requirement is what keeps that safe — without
   * it every lesson starting with a loose ball at your feet would re-arm on tick 1.
   */
  private repSpent(s: MatchState): boolean {
    if (this.resetOn !== 'outcome') return false;
    const settled = Math.hypot(s.ball.vel.x, s.ball.vel.y, s.ball.vel.z) < REP_SPENT_SPEED;
    if (!settled) return false;
    if (this.wasArmed && !isArmedRestart(s, this.armed)) return true; // the set piece has been played
    return this.touched && s.ball.possessor !== this.seat;
  }

  /**
   * True when the board should be re-armed. The caller re-runs `setup` and then calls `arm` again.
   * A scenario-armed restart counts as LIVE — it is the situation being rehearsed.
   */
  shouldReset(s: MatchState): boolean {
    this.noteTouch(s); // D152: tracked even when this scenario never re-arms — the hold reads it
    if (this.resetOn === 'never') return false;
    if ((s.phase === 'PLAY' && !this.repSpent(s)) || isArmedRestart(s, this.armed)) {
      this.deadSince = null;
      return false;
    }
    if (this.deadSince === null) {
      this.deadSince = s.tick;
      return false;
    }
    if (s.tick - this.deadSince < this.delayTicks) return false;
    this.deadSince = null;
    return true;
  }
}
